/**
 * CMS 訂單管理列表（GET /admin/orders）。
 *
 * 串 BE OrdersAdminController 列表 endpoint，提供：
 *   - 篩選：state / channel / 日期區間 / order_number 關鍵字
 *   - 分頁：limit 預設 25
 *   - 表格欄：order_number / channel / state（Pill）/ total / item_count / 首商品名 / created_at
 *   - 點列進詳情 /admin/orders/:id
 *
 * 對應 Dashboard KPI 之逐筆 drill-down；先前 CMS 僅有 KPI 聚合無逐筆。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill, type PillTone } from '../components/Pill';
import {
  listAdminOrders,
  ORDER_CHANNELS,
  ORDER_STATES,
  type AdminOrderListItem,
  type OrderChannel,
  type OrderState,
} from '../lib/api-client';

const STATE_TONE: Record<OrderState, PillTone> = {
  HOLD: 'warning',
  PAYMENT_PENDING: 'warning',
  PAID: 'success',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
  PAYMENT_FAILED: 'danger',
  OVERSOLD: 'danger',
};

const STATE_LABEL: Record<OrderState, string> = {
  HOLD: '保留中',
  PAYMENT_PENDING: '付款進行中',
  PAID: '已付款',
  EXPIRED: '已過期',
  CANCELLED: '已取消',
  PAYMENT_FAILED: '付款失敗',
  OVERSOLD: '超賣',
};

const CHANNEL_LABEL: Record<OrderChannel, string> = {
  web: 'Web',
  kiosk: 'Kiosk',
  partner: 'Partner',
  floor: 'Floor',
  recovery: 'Recovery',
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function formatTwd(amount: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `NT$ ${Math.round(n).toLocaleString('en-US')}`;
}

function pickI18n(o: Record<string, string> | null, fallback: string): string {
  if (!o) return fallback;
  return o['zh-TW'] ?? o['en'] ?? Object.values(o)[0] ?? fallback;
}

const PAGE_SIZE = 25;

export function OrdersListPage(): JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState<OrderState | ''>('');
  const [channel, setChannel] = useState<OrderChannel | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);

  const ordersQuery = useQuery({
    queryKey: ['admin.orders', state, channel, from, to, keyword, page],
    queryFn: () =>
      listAdminOrders({
        state: state || undefined,
        channel: channel || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to + 'T23:59:59').toISOString() : undefined,
        keyword: keyword.trim() || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    refetchInterval: 30_000,
  });

  const items: AdminOrderListItem[] = ordersQuery.data?.items ?? [];
  const total = ordersQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function resetFilters(): void {
    setState('');
    setChannel('');
    setFrom('');
    setTo('');
    setKeyword('');
    setPage(0);
  }

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">訂單管理</h1>
        <div className="page-head__actions">
          <Button
            variant="secondary"
            onClick={() => ordersQuery.refetch()}
            disabled={ordersQuery.isFetching}
          >
            {ordersQuery.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        全站訂單清單，含 KIOSK / Web / Partner 等通路；每 30 秒自動更新。點列進詳情看 items 與套用之
        pricing 規則。
      </p>

      <section
        className="card"
        style={{
          padding: '14px 18px',
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Field label="狀態">
          <select
            value={state}
            onChange={(e) => {
              setState(e.target.value as OrderState | '');
              setPage(0);
            }}
          >
            <option value="">全部</option>
            {ORDER_STATES.map((s) => (
              <option key={s} value={s}>
                {STATE_LABEL[s]}（{s}）
              </option>
            ))}
          </select>
        </Field>
        <Field label="通路">
          <select
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value as OrderChannel | '');
              setPage(0);
            }}
          >
            <option value="">全部</option>
            {ORDER_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="從">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(0);
            }}
          />
        </Field>
        <Field label="到">
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(0);
            }}
          />
        </Field>
        <Field label="order_number">
          <input
            type="search"
            placeholder="ORD-..."
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setPage(0);
            }}
            style={{ minWidth: 200 }}
          />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button variant="secondary" onClick={resetFilters}>
            清除
          </Button>
        </div>
      </section>

      {ordersQuery.isLoading && <div>載入中…</div>}
      {ordersQuery.error && (
        <div className="error-banner">取數失敗：{(ordersQuery.error as Error).message}</div>
      )}

      {ordersQuery.data && (
        <>
          <div className="table-card" data-testid="orders-table">
            <table>
              <thead>
                <tr>
                  <th>訂單編號</th>
                  <th>通路</th>
                  <th>狀態</th>
                  <th style={{ textAlign: 'right' }}>總價</th>
                  <th style={{ textAlign: 'right' }}>項數</th>
                  <th>首商品</th>
                  <th>建立時間</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      無符合條件之訂單
                    </td>
                  </tr>
                )}
                {items.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`order-row-${it.order_number}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/orders/${it.id}`)}
                  >
                    <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                      <Link
                        to={`/admin/orders/${it.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'var(--c-link)' }}
                      >
                        {it.order_number}
                      </Link>
                    </td>
                    <td>
                      <Pill dot={false}>
                        {CHANNEL_LABEL[it.channel as OrderChannel] ?? it.channel}
                      </Pill>
                    </td>
                    <td>
                      <Pill tone={STATE_TONE[it.state] ?? 'neutral'} dot={false}>
                        {STATE_LABEL[it.state] ?? it.state}
                      </Pill>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatTwd(it.total)}</td>
                    <td style={{ textAlign: 'right' }}>{it.item_count}</td>
                    <td
                      style={{
                        fontSize: 13,
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.first_product_name ? pickI18n(it.first_product_name, '—') : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {formatDateTime(it.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分頁列 */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              marginTop: 16,
              justifyContent: 'flex-end',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
              共 {total} 筆 · 第 {page + 1} / {Math.max(totalPages, 1)} 頁
            </span>
            <Button variant="secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              ← 上一頁
            </Button>
            <Button
              variant="secondary"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一頁 →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={{ color: 'var(--c-text-muted)' }}>{label}</span>
      {children}
    </label>
  );
}
